<!-- Floating robot button -->
<button class="ai-fab" type="button" (click)="openPanel()" aria-label="Open AI assistant" title="AI assistant">
  <span class="bob">
    <svg width="34" height="34" viewBox="0 0 40 40" fill="none">
      <g class="antenna">
        <line x1="20" y1="3" x2="20" y2="9" stroke="white" stroke-width="2" stroke-linecap="round" />
        <circle cx="20" cy="3" r="2.4" fill="#ec1c7a" stroke="white" stroke-width="1.4" />
      </g>
      <rect x="7" y="9" width="26" height="22" rx="6" fill="white" stroke="rgba(15,30,61,0.3)" stroke-width="1.2" />
      <rect x="10" y="13" width="20" height="13" rx="3" fill="#0f1e3d" />
      <circle class="eye" cx="16" cy="19.5" r="2" fill="#1bb6c1" />
      <circle class="eye" cx="24" cy="19.5" r="2" fill="#1bb6c1" />
      <path d="M16 23 Q20 25.5 24 23" stroke="#ec1c7a" stroke-width="1.4" stroke-linecap="round" fill="none" />
      <rect x="4" y="16" width="3" height="8" rx="1.5" fill="white" opacity=".85" />
      <rect x="33" y="16" width="3" height="8" rx="1.5" fill="white" opacity=".85" />
      <rect x="14" y="31" width="12" height="3" rx="1.5" fill="white" opacity=".7" />
    </svg>
  </span>
</button>

@if (open()) {
  <div class="ai-overlay" (click)="close()"></div>

  <div class="ai-panel" role="dialog" aria-label="AI assistant">
    <header class="ai-head">
      <span class="ai-head-avatar">
        <svg width="22" height="22" viewBox="0 0 40 40" fill="none">
          <line x1="20" y1="3" x2="20" y2="9" stroke="white" stroke-width="2" stroke-linecap="round" />
          <circle cx="20" cy="3" r="2.4" fill="#ec1c7a" />
          <rect x="7" y="9" width="26" height="22" rx="6" fill="white" />
          <rect x="10" y="13" width="20" height="13" rx="3" fill="#0f1e3d" />
          <circle cx="16" cy="19.5" r="2" fill="#1bb6c1" />
          <circle cx="24" cy="19.5" r="2" fill="#1bb6c1" />
          <path d="M16 23 Q20 25.5 24 23" stroke="#ec1c7a" stroke-width="1.4" stroke-linecap="round" fill="none" />
        </svg>
      </span>
      <span class="ai-head-text">
        <span class="ai-head-name">{{ assistantName() }}</span>
        <span class="ai-head-status"><span class="ai-dot-online"></span>Online · Ready to help</span>
      </span>
      <button class="ai-head-close" type="button" (click)="close()" aria-label="Close">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"
          stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </header>

    <!-- chat thread -->
    <div class="ai-thread" #thread>
      @for (message of messages(); track $index) {
        <div class="ai-msg" [class.user]="message.role === 'user'">
          @if (message.role === 'ai') {
            <span class="ai-msg-avatar">
              <svg width="15" height="15" viewBox="0 0 40 40" fill="none">
                <rect x="7" y="9" width="26" height="22" rx="6" fill="white" />
                <rect x="10" y="13" width="20" height="13" rx="3" fill="#0f1e3d" />
                <circle cx="16" cy="19.5" r="2" fill="#1bb6c1" />
                <circle cx="24" cy="19.5" r="2" fill="#1bb6c1" />
              </svg>
            </span>
          }
          <div class="ai-bubble">{{ message.text }}</div>
        </div>
      }

      @if (thinking()) {
        <div class="ai-msg">
          <span class="ai-msg-avatar">
            <svg width="15" height="15" viewBox="0 0 40 40" fill="none">
              <rect x="7" y="9" width="26" height="22" rx="6" fill="white" />
              <rect x="10" y="13" width="20" height="13" rx="3" fill="#0f1e3d" />
              <circle cx="16" cy="19.5" r="2" fill="#1bb6c1" />
              <circle cx="24" cy="19.5" r="2" fill="#1bb6c1" />
            </svg>
          </span>
          <div class="ai-bubble ai-typing"><span></span><span></span><span></span></div>
        </div>
      }

      @if (!selected()) {
        <!-- prompt picker -->
        <div class="ai-options">
          @for (prompt of prompts(); track prompt.title; let optionIndex = $index) {
            <!-- Title only; the description shows as a tooltip on hover / keyboard focus
                 and stays available to screen readers via aria-describedby. -->
            <button
              class="ai-option"
              type="button"
              [style.--ai-option-color]="prompt.color || '#ec1c7a'"
              [attr.aria-describedby]="prompt.description ? 'ai-option-desc-' + optionIndex : null"
              (click)="selectPrompt(prompt)"
            >
              @if (prompt.icon) {
                <span class="ai-option-icon"><mat-icon>{{ prompt.icon }}</mat-icon></span>
              }
              <span class="ai-option-title">{{ prompt.title }}</span>
              @if (prompt.description) {
                <span class="ai-option-desc" role="tooltip" [id]="'ai-option-desc-' + optionIndex">{{ prompt.description }}</span>
              }
            </button>
          } @empty {
            <p class="ai-empty">No assistant actions are available yet.</p>
          }
        </div>
      } @else {
        @if (awaitingAnswer() && currentOptions().length) {
          @switch (currentDisplay()) {
            @case ('dropdown') {
              <div class="ai-dropdown">
                <select (change)="answerFromSelect($event)">
                  <option value="" disabled selected>Select an option…</option>
                  @for (option of currentOptions(); track option.value) {
                    <option [value]="option.value">{{ option.label }}</option>
                  }
                </select>
              </div>
            }
            @case ('customdropdown') {
              <div class="ai-customdd">
                <button class="ai-customdd-trigger" type="button" (click)="toggleCustomDropdown()" [class.open]="customOpen()">
                  <span>Choose…</span>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
                    stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
                @if (customOpen()) {
                  <div class="ai-customdd-menu" role="listbox">
                    @for (option of currentOptions(); track option.value) {
                      <button class="ai-customdd-item" type="button" role="option" (click)="answerOption(option)" [title]="option.label">
                        @if (option.thumbnail) {
                          <img class="ai-customdd-thumb" [src]="option.thumbnail" [alt]="option.label" loading="lazy" />
                        }
                        <span class="ai-customdd-label">{{ option.label }}</span>
                      </button>
                    }
                  </div>
                }
              </div>
            }
            @default {
              <div class="ai-chips">
                @for (option of currentOptions(); track option.value) {
                  <button class="ai-chip" type="button" (click)="answerOption(option)">{{ option.label }}</button>
                }
              </div>
            }
          }
          @if (currentQuestion()?.hint; as hint) {
            <p class="ai-hint">{{ hint }}</p>
          }
        }
        @if (!thinking()) {
          <button class="ai-choose-another" type="button" (click)="chooseAnother()">← Choose another option</button>
        }
      }
    </div>

    <!-- static input bar -->
    <div class="ai-foot">
      <textarea
        #draftInput
        rows="1"
        class="ai-foot-input"
        placeholder="Type a message…"
        [value]="draft()"
        [disabled]="!awaitingAnswer()"
        (input)="draft.set(draftInput.value)"
        (keydown.enter)="$event.preventDefault(); sendDraft()"
      ></textarea>
      <button
        class="ai-foot-send"
        type="button"
        [disabled]="!awaitingAnswer() || !draft().trim()"
        (click)="sendDraft()"
        aria-label="Send"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
          stroke-linecap="round" stroke-linejoin="round">
          <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
        </svg>
      </button>
    </div>
  </div>
}
